import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { DataType } from "shared/types";

import {
  FilterDiffData,
  FilterOperator,
  FilterOverviewFormat,
  FilterValue,
} from "features/ui/Filters/types";
import { getFormattedFilter } from "features/ui/Filters/utils";
import Label from "features/ui/Label";

interface Props {
  fieldName: string;
  operator: FilterOperator;
  fieldValue: FilterValue;
  format?: FilterOverviewFormat;
  dense?: boolean;
  dataType?: DataType;
  diff?: FilterDiffData;
}

const DENSE_CLASSES = "text-xs!";

const FLabel = ({
  fieldName,
  operator,
  fieldValue,
  format = "badge",
  dense,
  dataType = DataType.STRING,
  diff,
}: Props) => {
  const { t } = useTranslation("filters");
  const { formattedFieldName, formattedOperator, formattedFieldValue } =
    getFormattedFilter(
      t,
      { fieldName, operator, fieldValue },
      format,
      dataType
    );

  const className = dense ? DENSE_CLASSES : "";

  const applyAttributeDiffStyle =
    diff?.changes[diff.level]?.[diff.groupIndex]?.[diff.rowIndex]?.attribute ??
    false;
  const applyOperatorDiffStyle =
    diff?.changes[diff.level]?.[diff.groupIndex]?.[diff.rowIndex]?.operator ??
    false;

  const labelText = (
    <span className="flex flex-row flex-wrap">
      <span
        className={classNames(
          "font-medium text-viaduct-black mr-0.75",
          applyAttributeDiffStyle ? diff?.bgColor : undefined
        )}
      >
        {formattedFieldName}
      </span>
      <span
        className={classNames(
          "italic font-normal text-gray-500",
          applyOperatorDiffStyle ? diff?.bgColor : undefined
        )}
      >
        {formattedOperator}
      </span>
      <span className={applyOperatorDiffStyle ? diff?.bgColor : undefined}>
        {formattedFieldValue !== "" && formattedOperator !== "" && ":"}
      </span>
    </span>
  );

  if (format === "label-inline") {
    // for EXISTS filters, display content vertically (underneath) instead of in two columns
    const shouldDisplayVertically =
      operator === FilterOperator.EXISTS ||
      operator === FilterOperator.NOT_EXISTS;

    if (shouldDisplayVertically) {
      return (
        <div className="flex w-full flex-col">
          <Label
            text={labelText}
            className={classNames(
              "pl-0! mb-0! inline-block whitespace-nowrap",
              className
            )}
          />
          <Label
            text={formattedFieldValue}
            className={classNames("text-viaduct-black max-w-lg", className)}
          />
        </div>
      );
    }

    return (
      <div className="flex w-full whitespace-nowrap flex-wrap">
        <Label
          text={labelText}
          className={classNames(
            "pl-0! mb-0! inline-block whitespace-nowrap",
            className
          )}
        />
        <Label
          text={formattedFieldValue}
          className={classNames(
            "text-viaduct-black mb-0! overflow-hidden text-ellipsis",
            // increase max width for occurs filter labels
            operator === FilterOperator.OCCURS ||
              operator === FilterOperator.NOT_OCCURS ||
              // TODO: find better solution for similarClusterID field
              ["Similar Cluster ID", "Similar cluster"].includes(fieldName)
              ? "max-w-96"
              : "max-w-60",
            className
          )}
        />
      </div>
    );
  }

  return (
    <span className={className}>
      <span className="capitalize">{formattedFieldName}</span>{" "}
      {formattedOperator} {formattedFieldValue}
    </span>
  );
};

export default FLabel;
